import React, { useState } from 'react'

import { Col, Row } from '@amzn/stencil-react-components/layout'
import { ProgressBar, ProgressBarStatus } from '@amzn/stencil-react-components/progress-bar'
import { Text } from '@amzn/stencil-react-components/text'

import { useInterval } from 'src/hooks/useInterval'
import { parseProgress } from 'src/pages/import-content/ImportContentModel'
import { CopyCell } from 'src/pages/module-builder/item-pool-editor/ItemPoolGenerator'
import { StatusWrapper } from 'src/pages/module-builder/SelfServeManualItemPoolBuilder/ManualItemPoolBuilderFinal'
import { GetProgressResponseStatus, ItemPoolService } from 'src/services/item-pools/ItemPoolService'

const DELAY_MS = 1000

const DONE_MESSAGE = 'Done'

export interface ImportedItemPoolProgressProps {
    versionId: string
    onSaved: () => void
    onError: (message: string) => void
}

// An import that skips Item Container creation saves its pool asynchronously: poll it like the
// manual and generated item pool builders, showing the backend's "x of y" progress as a bar.
export const ImportedItemPoolProgress = ({
    versionId,
    onSaved,
    onError,
}: ImportedItemPoolProgressProps) => {
    const [loading, setLoading] = useState(true)
    const [loadingMessage, setLoadingMessage] = useState('')
    const [progress, setProgress] = useState(0)
    const [failed, setFailed] = useState(false)

    useInterval(async () => {
        if (!loading) {
            return
        }
        const response = await ItemPoolService.getProgress(versionId)
        if (response.status === GetProgressResponseStatus.IN_PROGRESS) {
            setLoadingMessage(`Saving item pool: ${response.message}`)
            const parsed = parseProgress(response.message)
            if (parsed) {
                setProgress(parsed.done / parsed.total)
            }
            return
        }

        setLoading(false)
        if (response.status === GetProgressResponseStatus.IN_ERROR) {
            setFailed(true)
            setLoadingMessage('')
            onError(response.message)
        } else {
            setProgress(1)
            setLoadingMessage(DONE_MESSAGE)
            onSaved()
        }
    }, DELAY_MS)

    return (
        <Col gridGap='S300' dataTestId='imported-item-pool-progress'>
            <StatusWrapper loading={loading} loadingMessage={loadingMessage} />
            <ProgressBar
                progress={progress}
                status={
                    failed
                        ? ProgressBarStatus.Negative
                        : loading
                        ? ProgressBarStatus.Loading
                        : ProgressBarStatus.Positive
                }
                data-test-id='imported-item-pool-progress-bar'
            />
            {!loading && !failed && (
                <Row alignItems='center' gridGap='S300' dataTestId='imported-item-pool-version-id'>
                    <Text>Item pool version ID: {versionId}</Text>
                    <CopyCell data={versionId} index={0} />
                </Row>
            )}
        </Col>
    )
}
